import React from 'react';
import { Button, HStack, Text, useBreakpointValue } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { FiChevronLeft, FiChevronRight, FiMinus, FiPlus } from 'react-icons/fi';

interface PdfNavigationControlsProps {
	pageNumber: number;
	numPages: number;
	loading: boolean;
	onPrevPage: () => void;
	onNextPage: () => void;
	variant?: 'compact' | 'full' | 'expanded';
	showPageInfo?: boolean;
	// Optional zoom controls
	scale?: number;
	onZoomIn?: () => void;
	onZoomOut?: () => void;
	onZoomReset?: () => void;
}

/**
 * Reusable PDF navigation controls component
 * Supports different variants for different contexts
 */
const PdfNavigationControls: React.FC<PdfNavigationControlsProps> = ({
	pageNumber,
	numPages,
	loading,
	onPrevPage,
	onNextPage,
	variant = 'full',
	showPageInfo = true,
	scale,
	onZoomOut,
	onZoomIn,
	onZoomReset,
}) => {
	const isMobile = useBreakpointValue({ base: true, md: false });

	if (variant === 'compact') {
		return (
			<HStack justify="space-between" gap={1} mt={2} textStyle="micro">
				<Button borderRadius={30} size="xs" onClick={onPrevPage} disabled={pageNumber <= 1} variant="outline">
					<FiChevronLeft />
				</Button>

				{showPageInfo && (
					<Text fontSize="sm" color="fg.muted">
						{pageNumber} of {numPages}
					</Text>
				)}

				<Button
					borderRadius={30}
					size="xs"
					onClick={onNextPage}
					disabled={pageNumber >= numPages}
					variant="outline"
				>
					<FiChevronRight />
				</Button>
			</HStack>
		);
	}

	if (variant === 'expanded') {
		return (
			<>
				<HStack justify={{ base: 'space-between', md: 'center' }} gap={{ base: 2, md: 12 }} w="full">
					<Button
						borderRadius={30}
						size="md"
						onClick={onPrevPage}
						disabled={pageNumber <= 1 || loading}
						variant="solid"
						bg="gray.700"
						color="white"
						_hover={{ bg: 'gray.600' }}
					>
						<FiChevronLeft />
						Previous
					</Button>

					{!isMobile && showPageInfo && (
						<Text textStyle="label" color="white" textAlign="center">
							{loading ? 'Loading...' : `Page ${pageNumber} of ${numPages}`}
						</Text>
					)}

					{/* Zoom controls when handlers provided */}
					{(onZoomOut || onZoomIn || onZoomReset) && (
						<HStack gap={2}>
							{onZoomOut && (
								<IconButton
									size="sm"
									borderRadius={30}
									variant="solid"
									bg="gray.700"
									color="white"
									_hover={{ bg: 'gray.600' }}
									onClick={onZoomOut}
								>
									<FiMinus />
								</IconButton>
							)}
							{/*{onZoomReset && (*/}
							{/*	<Button size="sm" borderRadius={30} variant="outline" color="white" _hover={{ bg: 'gray.700' }} onClick={onZoomReset}>*/}
							{/*		Reset{typeof scale === 'number' ? ` (${Math.round(scale * 100)}%)` : ''}*/}
							{/*	</Button>*/}
							{/*)}*/}
							{onZoomIn && (
								<IconButton
									size="sm"
									borderRadius={30}
									variant="solid"
									bg="gray.700"
									color="white"
									_hover={{ bg: 'gray.600' }}
									onClick={onZoomIn}
								>
									<FiPlus />
								</IconButton>
							)}
						</HStack>
					)}

					<Button
						borderRadius={30}
						size="md"
						onClick={onNextPage}
						disabled={pageNumber >= numPages || loading}
						variant="solid"
						bg="gray.700"
						color="white"
						_hover={{ bg: 'gray.600' }}
					>
						Next
						<FiChevronRight />
					</Button>
				</HStack>
				{isMobile && showPageInfo && (
					<Text textStyle="label" color="white" textAlign="center">
						{loading ? 'Loading...' : `Page ${pageNumber} of ${numPages}`}
					</Text>
				)}
			</>
		);
	}

	// Default 'full' variant
	return (
		<HStack justify="space-between" w="full" px={0}>
			<Button
				borderRadius={30}
				size="sm"
				onClick={onPrevPage}
				disabled={pageNumber <= 1 || loading}
				variant="outline"
			>
				<FiChevronLeft />
				Previous
			</Button>

			{showPageInfo && (
				<Text textStyle="label">{loading ? 'Loading...' : `Page ${pageNumber} of ${numPages}`}</Text>
			)}

			<Button
				borderRadius={30}
				size="sm"
				onClick={onNextPage}
				disabled={pageNumber >= numPages || loading}
				variant="outline"
			>
				Next
				<FiChevronRight />
			</Button>
		</HStack>
	);
};

export default PdfNavigationControls;
